import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as OrderAction from "../../../stores/orders/OrderAction";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { ManageOrdersFilter } from "../../components/ManageOrdersFilter/ManageOrdersFilter";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useHistory, useParams } from "react-router-dom";
import Typography from "@material-ui/core/Typography/Typography";
import { styles } from "./ManageOrdersView.styles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import { fromToString } from "../../../utilities/dateTimeUtils";
import OrderBasicModel from "../../../models/order/OrderBasicModel";

export interface IManageOrdersViewProps extends WithStyles<typeof styles> {}

const ManageOrdersViewPure: React.FunctionComponent<IManageOrdersViewProps> = ({ classes }: IManageOrdersViewProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { userId } = useParams();
    const { t } = useTranslation();
    const isRequesting = useSelector(state => selectRequesting(state, [OrderAction.REQUEST_ALL_ORDERS_BASIC]));
    const allUsersFromState = useSelector(state => state.userDetails?.all);
    const productsOverview = useSelector(state => state.products);

    const [orderTypesChecked, setOrderTypesChecked] = useState<string[]>([]);
    const [orderStatesChecked, setOrderStatesChecked] = useState<string[]>([]);

    useEffect(() => {
        dispatch(OrderAction.requestAllOrdersBasic());
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [dispatch]);

    const isNothingSelected = (selectedArray: string[]) => {
        return selectedArray.length === 0;
    };

    const isUser = !!userId;
    const getUserDetails = (uId: string): any => {
        return allUsersFromState && Object.values(allUsersFromState)?.find((user: UserDetailsModel) => user.id === uId);
    };
    const userDetails = getUserDetails(userId);

    const handleOderTypeChange = (event: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
        setOrderTypesChecked(newTypes);
    };

    const handleOderStateChange = (event: React.MouseEvent<HTMLElement>, newStates: string[]) => {
        setOrderStatesChecked(newStates);
    };

    const handleCreateEmptyOrder = () => {
        history.push("/admin/manage-orders/create");
    };

    const handleOrderLinkClick = index => {
        const orderId = orders[index].orderId;
        const link = isUser ? `/admin/manage-users/${userId}/orders/${orderId}` : `/admin/manage-orders/${orderId}`;
        history.push(link);
    };

    let orders: IUniversalTableData = [];

    let allOrdersFromState = useSelector(state => state.orders.allBasic);
    if (allOrdersFromState) {
        let relevantlOrders: OrderBasicModel[] = Object.values(allOrdersFromState);
        if (!isNothingSelected(orderStatesChecked)) {
            relevantlOrders = relevantlOrders.filter(order => orderStatesChecked.includes(order.state));
        }
        if (!isNothingSelected(orderTypesChecked)) {
            relevantlOrders = relevantlOrders.filter(order => orderTypesChecked.includes(order.type));
        }
        if (isUser) {
            relevantlOrders = relevantlOrders.filter(order => order.userId === userId);
        }
        orders = relevantlOrders?.filter((order: OrderBasicModel) => order.state !== "New").map((order: OrderBasicModel) => ({
            orderId: order.orderId,
            userId: order.userId,
            email: getUserDetails(order.userId)?.email,
            created: order.created,
            updated: order.updated,
            state: order.state,
            type: order.type,
            timeRange: order.items.length === 1 ? fromToString(order.items[0]["from"], order.items[0]["to"]) : "",
            items: order.items
        }));
    }

    const columns: IUniversalTableColumn[] = [
        {
            type: "link",
            title: t("general.orderNo"),
            field: "orderId",
            onClick: index => handleOrderLinkClick(index)
        },
        {
            type: "text",
            title: t("general.user"),
            field: "email"
        },
        {
            type: "products",
            title: "Products",
            field: "items",
            allProducts: productsOverview.products
        },
        {
            type: "text",
            title: "Time range",
            field: "timeRange"
        },
        {
            type: "dateTime",
            title: t("general.createdOn"),
            field: "created"
        },
        {
            type: "dateTime",
            title: t("general.updatedOn"),
            field: "updated"
        },
        {
            type: "text",
            title: t("general.status"),
            field: "state"
        },
        {
            type: "text",
            title: t("general.type"),
            field: "type"
        }
    ];

    const userFullDetails = userDetails ? `${userDetails.firstName} ${userDetails.lastName} (${userDetails.email})` : "";

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={
                    isUser
                        ? [
                              { title: t("admin.adminPanel"), href: "/admin" },
                              { title: t("admin.manageUsersView.manageUsers"), href: "/admin/manage-users" },
                              {
                                  title: `${userFullDetails} - ${t("general.orders")}`
                              }
                          ]
                        : [
                              { title: t("admin.adminPanel"), href: "/admin" },
                              { title: t("admin.manageOrdersView.manageOrders") }
                          ]
                }
            />
            <div className={classes.title}>
                <Typography variant="h3">{t("admin.manageOrdersView.manageOrders")}</Typography>
            </div>
            <PermissionGuard scope="orders.write" >
                <BlueButton onClick={handleCreateEmptyOrder}>{t("admin.manageOrdersView.createEmptyOrder")}</BlueButton>
            </PermissionGuard>

            {isUser && userDetails && (
                <Typography variant="h5" className={classes.subTitle}>{`${t("general.user")}: ${userFullDetails}`}</Typography>
            )}

            <ManageOrdersFilter
                orderTypesChecked={orderTypesChecked}
                orderStatesChecked={orderStatesChecked}
                onOrderTypeCheckboxClick={handleOderTypeChange}
                onOrderStateCheckboxClick={handleOderStateChange}
            />
            <UniversalTable isLoading={isRequesting} columns={columns} data={orders} searchable />
        </CenteredContainer>
    );
};

export const ManageOrdersView = withStyles(styles)(ManageOrdersViewPure);
